import React, { useCallback, useMemo, useRef } from "react"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"
import { Flex, Image, Button, Text } from "@chakra-ui/react"
import { usePictureInPicture } from "../../../hooks/usePictureInPicture"
import { pipParamsSelector } from "../../../recoil/atoms/pictureInPicture"
import { OverlayModel } from "../../../recoil/atoms/overlay/types"
import { userListSelector } from "../../../recoil/atoms/users"
import { OverflownText } from "../../common/overflown-text"
import { brandingSelector } from "../../../recoil/atoms/room"
import { CgInternal } from "react-icons/cg"
import { Icon } from "../../common/icons/icon"
import { mainWindowUserSelector } from "../../../recoil/atoms/mainWindowUser"
import { usersSelector, localUserIdAtom } from "../../../recoil/atoms/users"
import {
  Stack,
  OverlayTitle,
  ImageContainer,
  OverlayContainer,
  OverlayInfoContainer,
  OverlayInfoWrapper,
  OverlayDescription,
  OverlayPriceWrapper,
  BottomContainerOverlay
} from "./badge.styled"
import { useBrowserType } from "../../../hooks/useBrowserType"
import { useLocalUserType } from "../../../hooks/useLocalUserType"
import { useWatchParty } from "../../../hooks/useWatchParty"
import { userOnProceedToBuyScreenSelection } from "../../../recoil/atoms/overlay"

import useOnClickWithTouchHandler from "../../../hooks/useOnClickWithTouchHandler"
import { ProductSelectorSizeOverlayType } from "./product-selector/types"
import { cleanProductDescription, stripHtmlTags, toPixelString } from "../../../util"
import { MarqueeButton } from "../../common/buttons/"
import { ProductVariant } from "./product-selector/types"
import { useTranslation } from "react-i18next"
import { CloseOverlayIcon } from "../../common/icons/close-overlay-icon"
import { productOverlaySelector } from "../../../recoil/atoms/product-overlay"
import useProductPageOverlay from "../../../hooks/useProductPageOverlay"
import { addHitAreaStyle } from "../../../util/styleUtils"
import useMeetingClient from "../../../hooks/useMeetingClient"
import useTextOverflowDetection from "../../../hooks/useTextOverflowDetection"

interface BadgeOverlayProps extends OverlayModel {
  isMobile?: boolean
  isTablet?: boolean
  variants?: ProductVariant[]
  width?: string | number
  maxWidthOverlay?: string | number
  isSmallMainScreen?: boolean
  isBadgeLiveMode?: boolean
  isTransperent?: boolean
  isTouch?: boolean
  isLandscape?: boolean
  isRedirectToUrl?: boolean
  isProductDelete?: boolean
  currencySymbol?: string
  productSelectorSizeOverlayProps?: ProductSelectorSizeOverlayType
  showCloseButton?: boolean
  stylesBadge?: {
    container: React.CSSProperties
  }
}

const BadgeOverlay = React.memo(
  ({
    id,
    title,
    description,
    currentPrice,
    oldPrice,
    image,
    checkoutUrl,
    buttonText,
    isMobile,
    width,
    maxWidthOverlay,
    coverImages,
    isSmallMainScreen,
    isBadgeLiveMode,
    isTransperent,
    isTouch,
    isLandscape,
    isRedirectToUrl,
    productSelectorSizeOverlayProps,
    isTablet,
    stylesBadge,
    vendor,
    showCloseButton = false,
    ...props
  }: BadgeOverlayProps) => {

    const meetingClient = useMeetingClient()
    const overlayContainerRef = React.useRef<HTMLDivElement>(null)
    const { t } = useTranslation()
    const { isWatchParty, isAudioWatchParty } = useWatchParty()

    const coverImage = React.useMemo(
      () =>
        props?.channel === "shopify" ? coverImages?.[0]?.src : coverImages?.[0],
      [coverImages, props?.channel]
    )

    const [
      {
        isOpen,
        productOldPrice,
        productImage,
        productButtonText,
        productCurrentPrice,
        productShortDescription,
        productDescription,
        productTitle,
      },
      setProductOverlay
    ] = useRecoilState(productOverlaySelector)

    const { isModerator } = useLocalUserType()
    const [{ isActivePiP, isRedirected }, setPiPParams] =
      useRecoilState(pipParamsSelector)

    const allUserList = useRecoilValue(userListSelector)
    const roomStyles = useRecoilValue(brandingSelector)
    const { openFullScreenProductPage } = useProductPageOverlay()
    const setIsUserOnProceedToBuyScreen = useSetRecoilState(
      userOnProceedToBuyScreenSelection
    )

    const mainWindowUser = useRecoilValue(mainWindowUserSelector)
    const mainWindowUserDetails = useRecoilValue(
      usersSelector(mainWindowUser.uid)
    )
    const localUserId = useRecoilValue(localUserIdAtom)
    const { isAndroidMobile, isFirefox } = useBrowserType()
    const prevBadgeOverlayIdRef = React.useRef(id)

    // following variable are use to detect the text overflow
    const priceTextEleRef = useRef(null);
    const oldPriceTextEleRef = useRef(null);
    const isPriceTextOverflowing = useTextOverflowDetection(priceTextEleRef, [currentPrice])
    const isOldPriceTextOverflowing = useTextOverflowDetection(oldPriceTextEleRef, [oldPrice])

    React.useEffect(() => {
      if (prevBadgeOverlayIdRef.current !== id) {
        exitPictureInPicture()
        setPiPParams({ isActivePiP: false })
        prevBadgeOverlayIdRef.current = id
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id])

    const selectedVariant = React.useMemo(() => {
      const variants = props.variants ?? []
      return variants ? variants[0] : {}
    }, [props.variants])

    const selectedImages = React.useMemo(() => {
      if (coverImages?.length !== 0) {
        const image = coverImages?.find(image => {
          return image?.variant_ids?.find(vId => vId === selectedVariant?.id)
        })
        return image ? [image.src] : []
      }
      return []
    }, [coverImages, selectedVariant?.id])

    const openOverlayProduct = React.useCallback(() => {
      setProductOverlay({
        isOpen: true,
        productTitle: title,
        productDescription: description,
        options: props.options ?? [],
        variants: props.variants ?? [],
        selectedImages: selectedImages,
        selectedVariant: selectedVariant,
        coverImages: coverImages,
        defaultImageSrc: coverImage,
        isProductDelete: props?.isProductDelete,
        currencySymbol: props.currencySymbol,
        checkoutUrl: checkoutUrl,
        isBadgeLiveMode: isBadgeLiveMode,
        action: "update",
        productImage: image,
        productCurrentPrice: currentPrice,
        productOldPrice: oldPrice,
        productButtonText: buttonText,
        productShortDescription: props.shortDescription,
        channel: props.channel
      })
    }, [
      buttonText,
      checkoutUrl,
      coverImage,
      coverImages,
      currentPrice,
      description,
      image,
      isBadgeLiveMode,
      oldPrice,
      props.currencySymbol,
      props?.isProductDelete,
      props.options,
      props.shortDescription,
      props.variants,
      selectedImages,
      selectedVariant,
      setProductOverlay,
      title,
      props.channel
    ])

    const {
      
      requestPictureInPicture,
      requestPictureInPictureWP,
      exitPictureInPicture
    } = usePictureInPicture()

    const mainCameraEnabled = useMemo(
      () => allUserList.some(user => user.camEnabled),
      [allUserList]
    )

    const [timeStamp, setTimeStamp] = React.useState({
      start: 0,
      end: 0
    })

    const handleTouchStart = e => {
      setTimeStamp({ ...timeStamp, start: e.timeStamp })
    }

    const handleClick = useCallback(
      e => {
        if (
          mainCameraEnabled &&
          !(
            isFirefox ||
            isAndroidMobile ||
            (mainWindowUser.uid === localUserId &&
              isMobile &&
              !isTablet &&
              !isWatchParty)
          )
        ) {
          requestPictureInPicture()
        }

        setPiPParams({
          isActivePiP: true,
          isRedirected: false,
          coverImage: coverImage
        })

        if (!isBadgeLiveMode) {
          openOverlayProduct()
        }

        if (isBadgeLiveMode) {
          openOverlayProduct()
        }

        e.stopPropagation()
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [setPiPParams, requestPictureInPicture, mainCameraEnabled, isWatchParty]
    )

    const handleWPPiP = useCallback(
      e => {
        requestPictureInPictureWP()

        setPiPParams({
          isActivePiP: true,
          isRedirected: false,
          coverImage: coverImage
        })
        if (!isBadgeLiveMode) {
          openOverlayProduct()
        }

        if (isBadgeLiveMode) {
          openOverlayProduct()
        }
        e.stopPropagation()
      },

      [
        requestPictureInPictureWP,
        setPiPParams,
        coverImage,
        isBadgeLiveMode,
        openOverlayProduct
      ]
    )

    /* const handleConfirm = useCallback(
      (
        event:
          | React.MouseEvent<HTMLButtonElement>
          | React.TouchEvent<HTMLButtonElement>
      ) => {
        event.stopPropagation()
        openExternalUrl(checkoutUrl)
      },
      [openExternalUrl, checkoutUrl]
    ) */

    const handleOpenProduct = React.useCallback(
      async e => {

        e.stopPropagation()

        // open product page
        openFullScreenProductPage({
          url: checkoutUrl,
          showVariantScreen: props.variants && props.variants.length > 0,
          openOverlayProduct,
          fallbackHandler: () => {
            // Open PIP way as a fallback
            if (isWatchParty && !isAudioWatchParty) {
              return handleWPPiP(e)
            }
            return handleClick(e)
          }
        })

        // Submit buy event click log to server
        meetingClient.userEventLog?.submitProductBuyClickEvent({
          productId: id
        })
      },
      [
        id,
        meetingClient,
        handleClick,
        openOverlayProduct,
        handleWPPiP,
        isAudioWatchParty,
        isWatchParty,
        openFullScreenProductPage,
        checkoutUrl,
        props.variants
      ]
    )

    const handleTouchEnd = React.useCallback(
      e => {
        if (e.timeStamp - timeStamp.start < 150) {
          handleOpenProduct(e)
        }
        setTimeStamp({ start: 0, end: 0 })
      },
      [timeStamp.start, handleOpenProduct]
    )

    const isBigBadge = React.useMemo(() => {
      return isBadgeLiveMode && oldPrice && currentPrice && !isSmallMainScreen
    }, [currentPrice, isBadgeLiveMode, isSmallMainScreen, oldPrice])

    const handleClosePIP = useCallback(
      e => {
        exitPictureInPicture()
        setPiPParams({ isActivePiP: false })
        e.stopPropagation()
      },
      [exitPictureInPicture, setPiPParams]
    )

    const closePipHandlers = useOnClickWithTouchHandler(handleClosePIP)

    const showImage = !(isActivePiP && !isRedirected && !isBadgeLiveMode)

    const isActive = isActivePiP
    React.useEffect(() => {
      setIsUserOnProceedToBuyScreen(isActive)
    }, [isActive, setIsUserOnProceedToBuyScreen])

    const isReturnToVideoButton =
      isActivePiP &&
      document.pictureInPictureEnabled &&
      (mainWindowUserDetails?.camEnabled || isWatchParty) &&
      !isBadgeLiveMode

    const fontSizePrice = React.useMemo<string>(() => {
      if (isMobile && !isTablet && isReturnToVideoButton && productOldPrice) {
        return "0.6rem"
      }

      if(isBadgeLiveMode) {
        if(currentPrice.length > 25) return "8px"
        if(currentPrice.length > 20) return "9px"
        if(currentPrice.length > 16) return "x-small"
        if(currentPrice.length >= 12) return "xs"
      }

      return isSmallMainScreen || isBigBadge ? "xs" : "sm"
    }, [
      isBadgeLiveMode,
      isBigBadge,
      currentPrice,
      isMobile,
      isReturnToVideoButton,
      isSmallMainScreen,
      isTablet,
      productOldPrice
    ])

    const descriptionOveralay = React.useMemo<string>(() => {
      if (isReturnToVideoButton) {
        return productShortDescription && productShortDescription.length !== 0
          ? productShortDescription
          : productDescription
      }
      return props.shortDescription && props.shortDescription.length !== 0
        ? props.shortDescription
        : description
    }, [
      description,
      isReturnToVideoButton,
      productDescription,
      productShortDescription,
      props.shortDescription
    ])

    const isCloseIconVisibile = (!isBadgeLiveMode || showCloseButton) &&
    !(isModerator && !isMobile) &&
    !isOpen &&
    !(isActivePiP && !isRedirected) 

    return (
      <OverlayContainer
        id={id}
        ref={overlayContainerRef}
        isMobile={isMobile}
        isProductOverlay={isOpen}
        roomStyles={roomStyles}
        isModerator={isModerator}
        isTransperent={isTransperent}
        isFullSizeProductOverlay={
          productSelectorSizeOverlayProps?.isFullSizeOverlay
        }
        isBadgeLiveMode={isBadgeLiveMode}
        width={
          isActivePiP && !isRedirected && !isBadgeLiveMode
            ? maxWidthOverlay
            : width
        }
        style={{
          ...stylesBadge?.container,
          height:
            isActivePiP &&
            !isRedirected &&
            !isBadgeLiveMode &&
            `calc(${productSelectorSizeOverlayProps?.height}px + 0px)`,
          maxWidth:
            isActivePiP &&
            document.pictureInPictureEnabled &&
            (mainWindowUserDetails?.camEnabled || isWatchParty) &&
            !isBadgeLiveMode &&
            !isOpen
              ? `${width}px`
              : isActivePiP && !isRedirected && !isBadgeLiveMode && "100%"
        }}
      >
        {isCloseIconVisibile && <CloseOverlayIcon isMobile={isMobile} />}
        <Stack style={{ height: isBadgeLiveMode && "100%" }}>
          {image !== "" && image?.length > 0 && showImage && (
            <ImageContainer isSmallMainScreen={isSmallMainScreen}>
              <Image
                src={isReturnToVideoButton ? productImage : image}
                alt={title}
                maxWidth={isSmallMainScreen ? "50px" : "75px"}
                maxHeight={isSmallMainScreen ? "50px" : "75px"}
                overflow="hidden"
                whiteSpace="nowrap"
                textOverflow="ellipsis"
              />
            </ImageContainer>
          )}
          {/*  {isActivePiP && !isRedirected && !isBadgeLiveMode ? ( */}

          {/*  ) : ( */}
          <OverlayInfoContainer
            isBadgeLiveMode={isBadgeLiveMode}
            isTransperent={isTransperent}
            style={{
              display:
                isActivePiP && !isRedirected && !isBadgeLiveMode
                  ? "none"
                  : "flex",
              width: `calc(100% - ${isSmallMainScreen ? "50px" : "75px"})`
            }}
          >
            <OverlayInfoWrapper roomStyles={roomStyles}>
              <OverlayTitle
                isSmallMainScreen={isSmallMainScreen}
                isTransperent={isTransperent}
                isMobile={isMobile}
                isBadgeLiveMode={isBadgeLiveMode}
                paddingRight={isCloseIconVisibile ? "26px" : null}
              >
                {isReturnToVideoButton ? productTitle : title}
              </OverlayTitle>
              <OverlayDescription
                isMobile={isMobile}
                isBigBadge={isBigBadge}
                isSmallMainScreen={isSmallMainScreen}
                isTransperent={isTransperent}
                isBadgeLiveMode={isBadgeLiveMode}
              >
                <OverflownText>
                  {cleanProductDescription(stripHtmlTags(descriptionOveralay))}
                </OverflownText>
              </OverlayDescription>
            </OverlayInfoWrapper>
            <BottomContainerOverlay>
              <OverlayPriceWrapper isBadgeLiveMode={isBadgeLiveMode}>
                <Flex
                  ref={priceTextEleRef}
                  fontSize={fontSizePrice}
                  fontWeight={"medium"}
                  display="inline"
                  mr={"0.2rem"}
                  // textOverflow="ellipsis"
                  overflow="hidden"
                  whiteSpace="nowrap"
                  color={
                    isTransperent
                      ? "white"
                      : roomStyles?.badgeTextColor && roomStyles?.badgeTextColor
                  }
                  maxW={
                    isReturnToVideoButton && isMobile && !isTablet && "55px"
                  }
                >
                   <span
                      style={{
                        display: 'inline-block',
                        animation: isPriceTextOverflowing ? 'marquee-anim 8s linear infinite' : 'none',
                       }}
                    >
                       {isReturnToVideoButton ? productCurrentPrice : currentPrice}
                   </span>
                 
                </Flex>
                {((isReturnToVideoButton && productOldPrice) ||
                  (!isReturnToVideoButton && oldPrice)) && (
                  <Flex
                    ref={oldPriceTextEleRef}
                    fontSize={fontSizePrice}
                    fontWeight={"medium"}
                    textDecoration={"line-through"}
                    color={
                      isTransperent
                        ? "white"
                        : roomStyles?.badgeTextColor ?? "black"
                    }
                    display="inline"
                    mr={"0.2rem"}
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    opacity="0.65"
                    maxW={
                      isReturnToVideoButton && isMobile && !isTablet && "55px"
                    }
                  >
                    <span
                      style={{
                        display: 'inline-block',
                        animation: isOldPriceTextOverflowing ? 'marquee-anim 8s linear infinite' : 'none',
                        textDecoration: "line-through"
                       }}
                    >
                      {isReturnToVideoButton ? productOldPrice : oldPrice}
                   </span>
                  </Flex>
                )}
              </OverlayPriceWrapper>
              <Flex
                alignItems="end" /* id="test" width="100%" justifyContent="end" */
                width={`${100 + (isReturnToVideoButton ? 100 : 0)}px`}
              >
                {isReturnToVideoButton && (
                  <Button
                    {...closePipHandlers}
                    colorscheme="teal"
                    variant="outline"
                    size={
                      isSmallMainScreen && !isMobile && !isTablet ? "xs" : "sm"
                    }
                    _focus={{}}
                    fontSize={
                      isSmallMainScreen && !isMobile && !isTablet ? "xs" : "sm"
                    }
                    whiteSpace="nowrap"
                    lineHeight="1"
                    height={isMobile && "25px"}
                    bg={
                      roomStyles?.badgeButtonBackgroundColor
                        ? roomStyles.badgeButtonBackgroundColor
                        : "gray.100"
                    }
                    textColor={
                      roomStyles?.badgeButtonTextColor
                        ? roomStyles.badgeButtonTextColor
                        : "#718096"
                    }
                    _hover={{
                      backgroundColor:
                        roomStyles?.badgeButtonMouseoverBackgroundColor
                          ? roomStyles.badgeButtonMouseoverBackgroundColor
                          : "#edf2f7",
                      textColor: roomStyles.badgeButtonMouseoverTextColor
                        ? roomStyles.badgeButtonMouseoverTextColor
                        : "#000",
                      borderColor: roomStyles?.badgeButtonBorderMouseoverColor || null,
                    }}
                    borderColor={
                      roomStyles?.badgeButtonBorderColor
                        ? roomStyles.badgeButtonBorderColor
                        : "#e2e8f0"
                    }
                    fontFamily={
                      roomStyles?.badgeButtonFontFamily
                        ? roomStyles.badgeButtonFontFamily
                        : "inherit"
                    }
                    borderRadius={toPixelString(roomStyles?.badgeButtonBorderRadius)}
                    width="100%"
                    overflow="hidden"
                    paddingLeft={!isMobile && !isSmallMainScreen && "1rem"}
                    paddingRight={!isMobile && !isSmallMainScreen && "1rem"}
                    marginRight="0.5rem"
                  >
                    <Icon
                      marginRight={!isMobile && !isSmallMainScreen && "0.2rem"}
                      fontSize={
                        isSmallMainScreen && !isMobile && !isTablet && "1.1rem"
                      }
                      src={CgInternal}
                      cursor="pointer"
                    />
                    {!isMobile && !isSmallMainScreen && (
                      /*  widthOverlayContainer > 370 && */ <Text>
                        {t("Return video")}
                      </Text>
                    )}
                  </Button>
                )}
                <MarqueeButton
                  textButton={
                    isReturnToVideoButton ? productButtonText : buttonText
                  }
                  customStyles={{
                    button: {
                      height: isMobile
                        ? "25px"
                        : isSmallMainScreen
                        ? "1.5rem"
                        : "2rem",
                      maxWidth: "100px",
                      marginLeft: !isBadgeLiveMode && "5px",
                      borderRadius: toPixelString(roomStyles?.badgeButtonBorderRadius),
                      _hoverStyles: isMobile
                        ? {}
                        : {
                            background:
                              roomStyles?.badgeButtonMouseoverBackgroundColor
                                ? roomStyles.badgeButtonMouseoverBackgroundColor
                                : "#edf2f7",
                            color: roomStyles.badgeButtonMouseoverTextColor
                              ? roomStyles.badgeButtonMouseoverTextColor
                              : "#000",
                            borderColor: roomStyles?.badgeButtonBorderMouseoverColor || null,
                          }
                    },
                    textButton: {
                      maxWidth: "100px",
                      padding: "0px 10px",
                      fontSize:
                        isSmallMainScreen && !isMobile && !isTablet
                          ? "0.75rem"
                          : "0.85rem"
                    }
                  }}
                  onTouchStart={
                    isTouch || (isWatchParty && isLandscape)
                      ? handleTouchStart
                      : null
                  }
                  onTouchEnd={
                    isTouch || (isWatchParty && isLandscape)
                      ? handleTouchEnd
                      : null
                  }
                  onClick={isTouch ? null : handleOpenProduct}
                  background={
                    roomStyles?.badgeButtonBackgroundColor
                      ? roomStyles.badgeButtonBackgroundColor
                      : "gray.100"
                  }
                  color={
                    roomStyles?.badgeButtonTextColor
                      ? roomStyles.badgeButtonTextColor
                      : "#718096"
                  }
                  borderColor={
                    roomStyles?.badgeButtonBorderColor
                      ? roomStyles.badgeButtonBorderColor
                      : "#e2e8f0"
                  }
                  fontFamily={
                    roomStyles?.badgeButtonFontFamily
                      ? roomStyles.badgeButtonFontFamily
                      : "inherit"
                  }
                  _focus={{}}
                  _active={{}}
                  // Add extra hit area for mobile buttons
                  {...(isMobile ? addHitAreaStyle({ top: 10, bottom: 6 }): {})}
                />
              </Flex>
            </BottomContainerOverlay>
          </OverlayInfoContainer>
          {/*  )} */}
        </Stack>
      </OverlayContainer>
    )
  }
)

export default BadgeOverlay
